import { useQuery } from '@tanstack/react-query'
import { useContext, useEffect } from 'react'
import { isAxiosError } from 'axios'
import styled from 'styled-components'

import { QUERY_KEY } from '../../../constants'

import { toast } from '../../../utils'

import { getWithdrawalAccount } from '../../../api/withdrawalAccount'

import { StoresContext } from '../../../provider/StoreProvider'

import { useUserInfo } from '../../../hooks/useUserInfo'

import { Head } from '../../../components/head/Head'
import { Wrap } from '../../../components/container/Wrap'
import { Content } from '../../../components/container/Content'
import { CenterWrap, RowBetween } from '../../../components/common'
import { BankIcon } from '../../../components/bankIcon'
import type { BankName } from '../../../components/bankIcon/BankIcon'
import { Button } from '../../../components/button'
import { Text } from '../../../components/text'
import { Footer } from '../../../components/footer'
import { Header } from '../../../components/header'
import { Spinner } from '../../../components/spinner/Spinner'
import { NoticeContainer } from '../../../components/noticeContainer/NoticeContainer'
import { TitleContainer } from '../../../components/titleContainer/TitleContainer'

const HEAD_INFO = {
  title: '출금 계좌 | 미술품 조각투자 플랫폼, 아티피오',
  ogTitle: '출금 계좌 | 미술품 조각투자 플랫폼, 아티피오',
  ogUrl: window.location.href,
}

export function WithdrawalAccount() {
  const {
    commonStore: { setShowTab, setInvalidAccess },
  } = useContext(StoresContext)

  const { isLogin, isCheckingLogin } = useUserInfo()

  const {
    data: withdrawalAccount,
    isPending,
    isError,
    error,
  } = useQuery({ queryKey: [QUERY_KEY.withdrawalAccount], queryFn: getWithdrawalAccount })

  useEffect(() => {
    setShowTab(false)
  }, [])

  useEffect(() => {
    if (!isLogin && !isCheckingLogin) setInvalidAccess(true)
  }, [isLogin, isCheckingLogin])

  useEffect(() => {
    if (isError && error) {
      if (isAxiosError(error)) {
        toast(error.response?.data.message, error, false)
      }
    }
  }, [isError, error])

  if (isPending || isError) {
    return (
      <>
        <Head {...HEAD_INFO} />
        <Wrap>
          <Header type="SUB" title="출금 계좌" />
          <CenterWrap>
            <Spinner />
          </CenterWrap>
        </Wrap>
      </>
    )
  }

  return (
    <>
      <Head {...HEAD_INFO} />
      <Wrap>
        <Header type="SUB" title="출금 계좌" />
        <Content backgroundColor="gray_1">
          <TitleContainer title="출금 계좌 정보">
            <BankInfoBox>
              <BankInfo>
                <BankIcon name={withdrawalAccount.bankName as BankName} size={46} />
                <Text size={18} weight={600}>
                  {withdrawalAccount.bankName}
                </Text>
              </BankInfo>
            </BankInfoBox>
            <AccountList>
              <RowBetween>
                <Text size={14} weight={600} color="text_secondary">
                  계좌번호
                </Text>
                <Text size={14} weight={500} lineHeight={22} color="text_secondary">
                  {withdrawalAccount.account}
                </Text>
              </RowBetween>
              <RowBetween>
                <Text size={14} weight={600} color="text_secondary">
                  예금주
                </Text>
                <Text size={16} weight={500} lineHeight={22} color="text_secondary">
                  {withdrawalAccount.bankOwner}
                </Text>
              </RowBetween>
            </AccountList>
          </TitleContainer>
          <NoticeContainer
            title="안내사항"
            list={[
              <Text size={14} lineHeight={22} color="text_secondary">
                출금 계좌는 서비스 가입자{' '}
                <Text size={14} weight={700} color="text_secondary">
                  본인 명의의 실 계좌만 등록/변경
                </Text>
                이 가능해요.
              </Text>,
              <Text size={14} lineHeight={22} color="text_secondary">
                <Text size={14} weight={700} color="text_secondary">
                  평생 계좌 번호(휴대폰 번호, 사업자 번호 등)
                </Text>
                는 등록이 불가능해요.
              </Text>,
              <Text size={14} lineHeight={22} color="text_secondary">
                계좌 개설일이 오래되어 계좌 번호가 바뀐 경우에는{' '}
                <Text size={14} weight={700} color="text_secondary">
                  새롭게 바뀐 계좌 번호로 등록
                </Text>
                하셔야 해요.
              </Text>,
            ]}
          />
        </Content>
        <Footer>
          <Button
            type="primary"
            size="large"
            block={true}
            href="/account/withdrawal/banks"
            routeProps={{ state: { prevPathname: window.location.pathname } }}
          >
            변경하기
          </Button>
        </Footer>
      </Wrap>
    </>
  )
}

const BankInfoBox = styled(RowBetween)`
  margin-bottom: 24px;
`

const BankInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const AccountList = styled.div`
  display: flex;
  gap: 8px;
  flex-direction: column;
`
